import * as React from 'react';
import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import { withTheme } from 'styled-components';
import { Table, TableBody, TableRow } from './Grid';
import DownloadControls from './DownloadControls';
import WatchListToggle from './WatchListToggle';
import DocumentDropDown from './DocumentDropDown';
import ChangeIndicator from './ChangeIndicator';
import StarRating from './StarRating';
import { ShareclassColumn } from './ShareclassRows';
import Icon from './Icon';
import Translate from './Translate';
import DynamicLink from './DynamicLink';

const starIconStyleProps = {
    height: '1.2em',
    width: '1.2em',
};

@inject('dataStore')
@observer
class ShareclassColumnValue extends Component<any> {
    @computed
    get selectedGroupIndex() {
        return this.props.selectedGroupMap.get(this.props.columnIndex);
    }

    @computed
    get column() {
        return this.props.column.groups
            ? this.props.column.groups[this.selectedGroupIndex]
            : this.props.column;
    }

    render() {
        const {
            theme,
            dataStore,
            shareClass,
            isShareclassDisabled = false,
        } = this.props;
        const { column } = this;

        let columnContent;
        // Determine which column types can be wrapper with an anchor
        let hasLink = false;
        if (['group', 'dropdown'].includes(column.type)) {
            const shareclassColumnGroupedTheme = isShareclassDisabled
                ? theme.ShareclassColumnGroupedDisabled
                : theme.ShareclassColumnGrouped;
            hasLink = true;
            columnContent = (
                <Table>
                    <TableBody>
                        <TableRow>
                            {column.values.map((c, cIndex) => (
                                <ShareclassColumn
                                    key={`${column.type}-${c.value}-${cIndex}`}
                                    paddingX="sm"
                                    paddingY="0"
                                    textAlign={c.textAlign || 'center'}
                                    width={c.width}
                                    whiteSpace={
                                        c.wrapText ? 'normal' : 'nowrap'
                                    }
                                    {...shareclassColumnGroupedTheme}
                                >
                                    {c.multiple ? (
                                        <Fragment>
                                            {c.data.map((d, i) => (
                                                <Fragment key={i}>
                                                    {d.showChange ? (
                                                        <ChangeIndicator
                                                            dataStore={
                                                                dataStore
                                                            }
                                                            showChange={
                                                                d.showChange
                                                            }
                                                            value={d.value}
                                                            separator={
                                                                d.separator
                                                            }
                                                        />
                                                    ) : (
                                                        <Fragment>
                                                            <Translate>
                                                                {d.value}
                                                            </Translate>
                                                            {d.separator}
                                                        </Fragment>
                                                    )}
                                                </Fragment>
                                            ))}
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            {c.data.showChange ? (
                                                <ChangeIndicator
                                                    dataStore={dataStore}
                                                    showChange={
                                                        c.data.showChange
                                                    }
                                                    value={c.data.value}
                                                />
                                            ) : (
                                                <Translate>
                                                    {c.data.value}
                                                </Translate>
                                            )}
                                        </Fragment>
                                    )}
                                </ShareclassColumn>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            );
        } else if (column.type === 'download') {
            columnContent = (
                <DownloadControls
                    count={shareClass.downloadCount}
                    display="inline-flex"
                    disabled={shareClass.downloadCount === 0}
                    theme={{
                        ...theme,
                        ...{
                            DownloadButton: theme.ShareclassRowDownloadButton,
                            DownloadButtonIcon:
                                theme.ShareclassRowDownloadButtonIcon,
                            DownloadCount: theme.ShareclassRowDownloadCount,
                        },
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        dataStore.downloadDocuments(
                            shareClass.shareClassDownloads
                        );
                    }}
                />
            );
        } else if (column.type === 'watchlist' && dataStore.watchListEnabled) {
            columnContent = (
                <WatchListToggle
                    onClick={e => {
                        e.stopPropagation();
                        shareClass.toggleWatchList();
                    }}
                    active={shareClass.inWatchList}
                />
            );
        } else if (column.type === 'document') {
            columnContent = (
                <Fragment>
                    {Array.isArray(column.value) && column.value.length > 0 ? (
                        <DocumentDropDown
                            documents={column.value}
                            documentSelected={shareClass.documentSelected}
                            toggleDownload={shareClass.toggleDownload}
                        >
                            <Icon
                                fileName="DocumentIcon"
                                height="1.4em"
                                width="1.4em"
                                {...theme.ShareclassRowDocumentIcon}
                            />
                        </DocumentDropDown>
                    ) : (
                        <Translate>
                            {dataStore.config.noDataPlaceholder}
                        </Translate>
                    )}
                </Fragment>
            );
        } else if (column.type === 'prices') {
            if (column.data.performanceCheck && !shareClass.hasPerformance) {
                columnContent = <Translate>{column.data.value}</Translate>;
            } else {
                columnContent = (
                    <DownloadControls
                        display="inline-flex"
                        onClick={e => {
                            e.stopPropagation();
                            shareClass.getPriceHistory(column);
                        }}
                        count={1}
                        showCount={false}
                    />
                );
            }
        } else if (column.type === 'stars' || column.type === 'starsOutOf') {
            if (column.data.performanceCheck && !shareClass.hasPerformance) {
                columnContent = <Translate>{column.data.value}</Translate>;
            } else {
                columnContent = (
                    <StarRating
                        rating={column.data.value}
                        noDataPlaceholder={column.noDataPlaceholder}
                        showEmptyStars={column.type === 'starsOutOf'}
                        display="block"
                        theme={{
                            ...theme,
                            ...theme.StarRatingColumnWrapper,
                            StarIcon: {
                                ...starIconStyleProps,
                                ...theme.StarIconColumn,
                            },
                            StarOutlineIcon: {
                                ...starIconStyleProps,
                                ...theme.StarOutlineIconColumn,
                            },
                        }}
                    />
                );
            }
        } else if (column.multiple) {
            hasLink = true;
            columnContent = (
                <Fragment>
                    {column.data.map((d, i) => (
                        <Fragment key={i}>
                            {d.showChange ? (
                                <ChangeIndicator
                                    dataStore={dataStore}
                                    showChange={d.showChange}
                                    value={d.value}
                                    separator={d.separator}
                                />
                            ) : (
                                <Fragment>
                                    <Translate>{d.value}</Translate>
                                    {d.separator}
                                </Fragment>
                            )}
                        </Fragment>
                    ))}
                </Fragment>
            );
        } else {
            hasLink = true;
            columnContent = (
                <Fragment>
                    {column.data.showChange ? (
                        <ChangeIndicator
                            dataStore={dataStore}
                            showChange={column.data.showChange}
                            value={column.data.value}
                        />
                    ) : (
                        <Translate>{column.data.value}</Translate>
                    )}
                </Fragment>
            );
        }

        if (hasLink && column.url) {
            return (
                <DynamicLink
                    config={column}
                    shareClass={shareClass.shareClass}
                    dataStore={dataStore}
                    {...theme.ShareclassColumnLink}
                >
                    {columnContent}
                </DynamicLink>
            );
        }

        return columnContent;
    }
}

export default withTheme(ShareclassColumnValue) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassColumnValue.tsx